import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { FC, useMemo } from 'react'

import { MAX_DESKTOP_WIDTH, MAX_MOBILE_WIDTH } from '@src/quince-site-common/src/constants/app'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import ProductTag from '@src/quince-site-common/src/domains/ProductTag'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'

import { Icon, Image, ImageRefType } from '../../../atoms'
import { TrackConfig } from '../../../hocs/withTracking'
import { ProductCardAddToWishlist } from '../product-card-actions/add-to-wishlist'
import { ProductCardBuyItNow } from '../product-card-actions/buy-it-now'
import { ProductCardDeleteFromWishlist } from '../product-card-actions/delete-wishlist'
import { useProductCardHover } from '../product-card-context/product-card-hover.context'
import { useProductCardContext } from '../product-card-context/product-card.context'

import styles from './product-image-section.module.scss'
import { ProductTags } from './product-tags'
import { shouldShowBuyNowBasedOnDeviceType } from './utils'

export interface ProductImageSectionProps {
  gender?: GenderName | undefined
  imageRef?: ImageRefType
  maxWidth?: number
  eagerLoadImage?: boolean

  // for sub-sections
  wishlistIconClassName?: string
  wishlistTrackConfig?: Record<string, any>
  addToCartClassName?: string
  showDeleteBtn?: boolean
  deleteItemClassName?: string
  quality?: number
  topLeftIconSVG?: ((params: any) => JSX.Element) | null
  productClickTrackConfig?: TrackConfig
}

export const ProductImageSection: FC<
  ProductImageSectionProps & {
    className?: string
    applicableTags?: ProductTag[]
    spid?: number
    hideWishlistIcon?: boolean
    hideBuyNowIcon?: boolean
    hideTags?: boolean
    showSecondImageOnProductTiles?: boolean
  }
> = props => {
  const { t } = useTranslation()
  const { isHovered, setHovered } = useProductCardHover()
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const DEFAULT_MAX_PRODUCT_CARD_WIDTH = isMobile ? MAX_MOBILE_WIDTH : MAX_DESKTOP_WIDTH / 2

  const productImageProps = useProductImageProps(props.gender, isMobile, isHovered, props.showSecondImageOnProductTiles)

  return (
    <div
      className={classnames(styles.imageSection, props.className)}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
    >
      <div className={styles.imageContainer}>
        <Image
          alt={productImageProps.alt ?? t('product_image')}
          {...productImageProps}
          quality={props.quality}
          ref={props.imageRef}
          className={styles.productImage}
          maxWidth={props.maxWidth ?? DEFAULT_MAX_PRODUCT_CARD_WIDTH}
          // @i18n-ignore
          loading={props.eagerLoadImage ? 'eager' : 'lazy'}
          showSecondImageOnProductTiles={props.showSecondImageOnProductTiles}
        />
      </div>

      <div className={styles.imageOverlay}>
        {shouldShowBuyNowBasedOnDeviceType(isMobile) && !props.hideBuyNowIcon ? (
          <ProductCardBuyItNow
            addToCartClassName={classnames(props.addToCartClassName)}
            productClickTrackConfig={props.productClickTrackConfig}
          />
        ) : null}
        {!props.hideWishlistIcon && (
          <ProductCardAddToWishlist
            wishlistIconClassName={props.wishlistIconClassName}
            wishlistTrackConfig={props.wishlistTrackConfig}
            applicableTags={props.applicableTags}
          />
        )}
        {!isMobile && props.showDeleteBtn ? (
          <ProductCardDeleteFromWishlist
            deleteItemClassName={classnames(props.deleteItemClassName, styles.deleteBtn)}
          />
        ) : null}
        {props.hideTags ? null : (
          <ProductTags applicableTags={props.applicableTags} spid={props.spid} position={TAG_POSITION.BOTTOM} />
        )}
        {props.topLeftIconSVG ? (
          <div className={styles.awardIcon}>
            <Icon iconSvg={props.topLeftIconSVG} className={styles.iconStyles} />
          </div>
        ) : null}
      </div>
    </div>
  )
}

// utility functions/hooks
function useProductImageProps(
  gender: GenderName | undefined,
  isMobile: boolean,
  isHovered: boolean,
  showSecondImageOnProductTiles?: boolean,
) {
  const { product, selectedOptions } = useProductCardContext()
  const productImageProps = useMemo(() => {
    let [firstImage, secondImage = firstImage] = productHelper.getImagesForOptions(product, selectedOptions, gender)
    if ('dynamicImages' in product && !!product.dynamicImages) {
      ;[secondImage, firstImage] = [firstImage, secondImage]
    }
    const imageProps = ImageDomain.makeImageProps(
      isHovered == Boolean(showSecondImageOnProductTiles) ? firstImage?.image : secondImage?.image,
    )

    imageProps.title = ''
    imageProps.description = ''
    return imageProps
  }, [product, selectedOptions, gender, isMobile, isHovered, showSecondImageOnProductTiles])
  return productImageProps
}
